import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Smartphone, 
  Key, 
  CheckCircle, 
  XCircle, 
  Copy, 
  RefreshCw,
  Loader2,
  AlertTriangle,
  Eye,
  EyeOff
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Alert,
  AlertDescription,
} from './ui/alert';
import { toast } from 'sonner';
import { twoFactorAPI } from '../api/api';

const TwoFactorSettings = () => {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [enabledAt, setEnabledAt] = useState(null);
  
  // Setup state
  const [setupMode, setSetupMode] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  
  // Backup codes
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodesLoading, setBackupCodesLoading] = useState(false);
  
  // Disable dialog
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await twoFactorAPI.getStatus();
      if (response.data.success) {
        setEnabled(response.data.enabled);
        setEnabledAt(response.data.enabled_at);
      }
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = async () => {
    try {
      setSetupLoading(true);
      const response = await twoFactorAPI.setup();
      if (response.data.success) {
        setQrCode(response.data.qr_code);
        setSecret(response.data.secret);
        setSetupMode(true);
      }
    } catch (error) {
      toast.error('Failed to start 2FA setup');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    try {
      setSetupLoading(true);
      const response = await twoFactorAPI.enable(verificationCode);
      if (response.data.success) {
        toast.success('Two-factor authentication enabled!');
        setBackupCodes(response.data.backup_codes);
        setShowBackupCodes(true);
        setEnabled(true);
        setSetupMode(false);
        setVerificationCode('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword || !disableCode) {
      toast.error('Please enter your password and verification code');
      return;
    }

    try {
      setDisableLoading(true);
      const response = await twoFactorAPI.disable(disablePassword, disableCode);
      if (response.data.success) {
        toast.success('Two-factor authentication disabled');
        setEnabled(false);
        setShowDisableDialog(false);
        setDisablePassword('');
        setDisableCode('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setDisableLoading(false);
    }
  };

  const handleViewBackupCodes = async () => {
    try {
      setBackupCodesLoading(true);
      const response = await twoFactorAPI.getBackupCodes();
      if (response.data.success) {
        setBackupCodes(response.data.codes);
        setShowBackupCodes(true);
      }
    } catch (error) {
      toast.error('Failed to fetch backup codes');
    } finally {
      setBackupCodesLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    const code = prompt('Enter your current 2FA code to regenerate backup codes:');
    if (!code) return;

    try {
      setBackupCodesLoading(true);
      const response = await twoFactorAPI.regenerateBackupCodes(code);
      if (response.data.success) {
        setBackupCodes(response.data.backup_codes);
        toast.success('Backup codes regenerated');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate backup codes');
    } finally {
      setBackupCodesLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Two-Factor Authentication
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Add an extra layer of security to your account
            </p>
          </div>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
          enabled 
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
        }`}>
          {enabled ? (
            <>
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Enabled</span>
            </>
          ) : (
            <>
              <XCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Disabled</span>
            </>
          )}
        </div>
      </div>

      {/* Status Card */}
      <div className={`p-6 rounded-xl border ${
        enabled 
          ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' 
          : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'
      }`}>
        {enabled ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-300">
                  Your account is protected with 2FA
                </p>
                <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                  You'll be asked for a verification code when signing in from a new device.
                </p>
                {enabledAt && (
                  <p className="text-xs text-green-500 mt-2">
                    Enabled on {new Date(enabledAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleViewBackupCodes}
                disabled={backupCodesLoading}
                className="border-green-300 text-green-700 hover:bg-green-100 dark:border-green-700 dark:text-green-400"
                data-testid="view-backup-codes-btn"
              >
                {backupCodesLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Key className="w-4 h-4 mr-2" />
                )}
                View Backup Codes
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowDisableDialog(true)}
                className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400"
                data-testid="disable-2fa-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Disable 2FA
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-300">
                  Two-factor authentication is not enabled
                </p>
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                  Protect your account by adding an extra layer of security. You'll need an 
                  authenticator app like Google Authenticator or Authy.
                </p>
              </div>
            </div>
            <Button 
              onClick={handleStartSetup}
              disabled={setupLoading}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              data-testid="enable-2fa-btn"
            >
              {setupLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Shield className="w-4 h-4 mr-2" />
              )}
              Enable Two-Factor Authentication
            </Button>
          </div>
        )}
      </div>

      {/* Setup Mode */}
      {setupMode && (
        <div className="p-6 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 space-y-6">
          <h4 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-blue-500" />
            Set Up Authenticator App
          </h4>

          <div className="grid md:grid-cols-2 gap-6">
            {/* QR Code */}
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                1. Scan this QR code with your authenticator app:
              </p>
              <div className="bg-white p-4 rounded-lg inline-block border border-gray-200">
                <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
              </div>
              <div className="space-y-2">
                <p className="text-xs text-gray-500">Or enter this code manually:</p>
                <div className="flex items-center gap-2">
                  <code className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono flex-1">
                    {secret}
                  </code>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyToClipboard(secret)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Verification */}
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                2. Enter the 6-digit code from your authenticator app:
              </p>
              <div>
                <Label htmlFor="verification-code">Verification Code</Label>
                <Input
                  id="verification-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono mt-2"
                  data-testid="2fa-verification-input"
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSetupMode(false);
                    setVerificationCode('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleEnable2FA}
                  disabled={setupLoading || verificationCode.length !== 6}
                  className="bg-gradient-to-r from-green-500 to-emerald-600"
                  data-testid="verify-enable-2fa-btn"
                >
                  {setupLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Verify & Enable
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Backup Codes
            </DialogTitle>
            <DialogDescription>
              Save these codes in a secure place. Each code can only be used once.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-amber-700 dark:text-amber-300">
                Store these codes safely. If you lose access to your authenticator app, 
                you can use these codes to sign in.
              </AlertDescription>
            </Alert>
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, idx) => (
                <div 
                  key={idx}
                  className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded font-mono text-sm text-center"
                >
                  {code}
                </div>
              ))}
            </div>
            <div className="flex justify-between">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => copyToClipboard(backupCodes.join('\n'))}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy All
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleRegenerateBackupCodes}
                disabled={backupCodesLoading}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerate
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowBackupCodes(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              This will remove the extra security layer from your account. 
              You'll need to enter your password and a verification code.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="disable-password">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="disable-password"
                  type={showPassword ? 'text' : 'password'}
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder="Enter your password"
                  data-testid="disable-2fa-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="disable-code">Verification Code</Label>
              <Input
                id="disable-code"
                type="text"
                inputMode="numeric"
                maxLength={8}
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                placeholder="6-digit code or backup code"
                className="mt-1"
                data-testid="disable-2fa-code"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisableDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDisable2FA}
              disabled={disableLoading}
              data-testid="confirm-disable-2fa-btn"
            >
              {disableLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Disable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TwoFactorSettings;
